import * as React from "react";
import moment from "moment";
import { LabClient, WorkQueueClient, LabReadModel, WorkQueueItemModel, TaskModel, WorkersAndTasksModel, NodeUsageSummary, RunTypeClient, QueriesClient } from "@app/api2/api";
import { Progress, Tab, Button, Container, Table, TableRow, TableBody, TableCell, TableHeader, TableHeaderCell, TabPane, Select } from "@/components/bp/bp-ui";
import { BPColumn } from "@common/BPReactTable";
import { WorkQueueItem } from "./WorkQueueItem";
import { PageHeader } from "@common/PageHeader";
import { Link } from "react-router-dom";
import { buildUrl, AppRoutes } from "../AppRoutes";
import { bytesToMb, getLabFilter2 } from "@common/Utils";
import { BPReactTable2, getFilterValue, useBpReactTable2 } from "../Common/DataTable/BPReactTable2";
import { useTranslation2 } from "../Contexts/TranslationContext2";
import { ColumnDef } from "@tanstack/react-table";
import { runTypeClient, workQueueClient } from "@app/api2/Clients";
import { useQuery } from "@tanstack/react-query";
import { BPDataTable3 } from "../Common/DataTable/BPDataTable3";
import { BPButton } from "@/components/bp/bpbutton";
import { BPContainer } from "@/components/bp/bpcontainer";


export const refreshInterval = 2 * 60 * 1000;




class WorkQueueState {
    public loading: boolean = false;
    public labs: LabReadModel[] = [];
    areWorkersEnabled: boolean = false;
    labId: number = 0;
    runTypes: string[];
}

export class WorkQueue extends React.Component<{}, WorkQueueState> {
    private labClient = new LabClient();
    private workQueueClient = new WorkQueueClient();
    private runTypeClient = new RunTypeClient();
    private queriesClient = new QueriesClient();

    constructor(props, context) {
        super(props, context);
        this.state = new WorkQueueState();
    }

    public async componentDidMount(): Promise<any> {
        const labs = await this.queriesClient.getAllLabs();
        const workersEnabled = await this.workQueueClient.areWorkersEnabled();
        const runTypes = (await this.runTypeClient.getAll(false))?.runTypes?.map(x => x.runType);
        // const calculationTypes = (await this.runTypeClient.getChildren())?.calculationTypes?.map(x => x.runType);

        this.setState({
            ...this.state,
            labs: labs.labs,
            areWorkersEnabled: workersEnabled,
            runTypes: runTypes
        })
    }

    public render() {

        const panes = [
            { menuItem: window.translation.common.workQueue, render: () => <TabPane><WorkQueueFC /></TabPane> },
        ];

        if (this.state.areWorkersEnabled)
            panes.push({ menuItem: window.translation.workQueue.workers, render: () => <TabPane><WorkersComponent /></TabPane> });

        return (
            <BPContainer>
                <PageHeader header={window.translation.common.workQueue} />
                <Tab panes={panes} menu={{ secondary: true, pointing: true }} />
            </BPContainer>
        )
    }
}

export class WorkersComponent extends React.Component<{}, { model: WorkersAndTasksModel, workerNodes: Array<NodeUsageSummary> }> {
    private workQueueClient = new WorkQueueClient();

    constructor(props, context) {
        super(props, context);
        this.state = { model: null, workerNodes: null };
    }


    public componentDidMount() {
        this.loadNodes();
        this.loadWorkers();
    }

    private async loadWorkers() {
        const result = await this.workQueueClient.getWorkers();

        this.setState({
            model: result,
        });
    }

    private async loadNodes() {
        const workerNodes = await this.workQueueClient.getWorkerNodes();

        this.setState({
            workerNodes: workerNodes
        });
    }


    public async restartWorker(workerName: string) {
        await this.workQueueClient.restartWorker(workerName);
    }

    public async revoke(taskId: string) {
        await this.workQueueClient.revokeTask(taskId);
        await this.loadWorkers();
    }

    render() {
        const columns: BPColumn<TaskModel>[] = [
            {
                Header: window.translation.workQueue.workerTaskId,
                accessor: "taskId",
            },
            {
                Header: window.translation.caseRun.step,
                accessor: "phase",
            },
            {
                Header: window.translation.workQueue.workerName,
                accessor: "workerName",
            },
            //{
            //    cell: c => moment(c.startTime),
            //    name: window.translation.workQueue.,
            //    selector: "startTime",
            //},
            {
                Cell: c => <BPButton negative onClick={() => this.revoke(c.row.original.taskId)}>{window.translation.common.delete}</BPButton>,
                Header: "",
                id: "btn"
            },
        ];

        return (<div>
            <>
                {this.state.workerNodes?.map(node =>
                    <Table key={node.nodeName}>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>
                                    Name
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    CPU<br />
                                    Cores: {node.cpuTotalCores}
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Memory<br />
                                    Total: {bytesToMb(node.memoryTotalBytes)}
                                </TableHeaderCell>
                                <TableHeaderCell></TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            <TableRow key={node.nodeName}>
                                <TableCell>
                                    {node.nodeName}
                                </TableCell>
                                <TableCell>
                                    <span title={node.cpuUsedRaw}>
                                        {node.cpuUsedRatio.toLocaleString("en", { style: 'percent', minimumFractionDigits: 2 })}
                                    </span>
                                </TableCell>
                                <TableCell>
                                    <span title={node.memoryUsedRaw}>
                                        {node.memoryUsedRatio.toLocaleString("en", { style: 'percent', minimumFractionDigits: 2 })} ({bytesToMb(node.memoryUsedBytes)})
                                    </span>
                                </TableCell>
                            </TableRow>
                            {node.workers.map(w => (
                                <TableRow key={w.workerName}>
                                    <TableCell>
                                        {w.workerName}
                                    </TableCell>
                                    <TableCell>
                                        <span title={w.cpuUsageRaw}>
                                            {w.cpuUsageRatio.toLocaleString("en", { style: 'percent', minimumFractionDigits: 2 })}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span title={w.memoryUsageRaw}>
                                            {w.memoryUsageRatio.toLocaleString("en", { style: 'percent', minimumFractionDigits: 2 })} ({bytesToMb(w.memoryUsageBytes)})
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <BPButton icon={'redo'} negative content={'Restart Node'} onClick={() => {
                                            this.restartWorker(w.workerName);
                                            this.loadNodes();
                                        }}></BPButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                )}
                {this.state.model && <>
                    <h3>Active</h3>
                    <BPDataTable3
                        simple
                        noHeader={true}
                        columns={columns}
                        dataSource={() => Promise.resolve({ items: this.state.model?.activeTasks, totalRows: 1000 })} />
                    <h3>Reserved</h3>

                    <BPDataTable3
                        simple
                        noHeader={true}
                        columns={columns}
                        dataSource={() => Promise.resolve({ items: this.state.model?.reserverdTasks, totalRows: 1000 })} />
                    <h3>Scheduled</h3>
                    <BPDataTable3
                        simple
                        noHeader={true}
                        columns={columns}
                        dataSource={() => Promise.resolve({ items: this.state.model?.scheduledTasks, totalRows: 1000 })} />
                    <h3>Revoked</h3>
                    <BPDataTable3
                        simple
                        noHeader={true}
                        columns={columns}
                        dataSource={() => Promise.resolve({ items: this.state.model?.revokedTasks, totalRows: 1000 })} />
                </>
                }
            </>
        </div>
        );
    }
}

function WorkQueueFC() {
    const { translations: t } = useTranslation2();
    const runTypes = useQuery({
        queryKey: ['runTypes'],
        queryFn: async () => (await runTypeClient.getAll(false)).runTypes?.map(x => x.runType).map(key => { return { value: key, text: t.runType[key] } })
    });

    const statusOptions = React.useMemo(() => [
        { id: 'active', text: t.common.active },
        { id: 'created', text: t.common.created },
        { id: 'queued', text: t.workQueue.queued },
        { id: 'completed', text: t.common.completed },
        { id: 'failed', text: t.workQueue.checkData },
    ], [t]);

    const columns = React.useMemo<ColumnDef<WorkQueueItemModel>[]>(() => [
        getLabFilter2('labId', t.common.labs),
        {
            id: 'labName',
            header: t.common.lab,
            accessorFn: row => row.labName,
            cell: cell => <Link to={buildUrl(AppRoutes.LabDetails, { labId: cell.row.original.labId })}>{cell.getValue<string>()}</Link>,
        },
        {
            id: 'caseName',
            header: t.common.case,
            accessorFn: row => row.caseName,
            cell: (cell) => <Link to={buildUrl(AppRoutes.CaseDetails, { labId: cell.row.original.labId, caseId: cell.row.original.caseId })}>{cell.getValue<string>()}</Link>,
            meta: {
                width: '20%',
                cellTitle: true
            }
        },
        {
            header: window.translation.common.run,
            accessorFn: row => row.caseRunId,
            cell: (cell) => {
                if (cell.row.original.evidenceId != null)
                    return <Link to={buildUrl(AppRoutes.EvidenceDetails, {
                        caseId: cell.row.original.caseId,
                        labId: cell.row.original.labId,
                        evidenceId: cell.row.original.evidenceId
                    })}>{cell.row.original.evidenceId}</Link>;

                else if (cell.row.original.calculationId == null)
                    return cell.getValue() ? <Link to={buildUrl(AppRoutes.CaseRunDetails, { labId: cell.row.original.labId, caseId: cell.row.original.caseId, caseRunId: cell.row.original.caseRunId })}>{cell.getValue<number>()}</Link> : <></>;
                else
                    return <Link to={buildUrl(AppRoutes.CaseRunCalculationDetails, {
                        calculationId: cell.row.original.calculationId,
                        caseId: cell.row.original.caseId,
                        caseRunId: cell.row.original.caseRunId,
                        labId: cell.row.original.labId
                    })}><>{cell.getValue()}</></Link>;
            },
            meta: {
                width: '10%',
                cellTitle: true
            }
        },
        {
            accessorKey: "runTypeId",
            cell: (cell) => <span>{t.runType[cell.getValue<number>()]}</span>,
            header: t.workQueue.jobType,
            meta: {
                width: '15%',
                cellTitle: true,
                Filter: (column) =>
                    <Select
                        placeholder={t.common.runType}
                        style={{ width: '250px' }}
                        clearable
                        value={column.getFilterValue()?.toString()}
                        onChange={(ev, { value }) => { column.setFilterValue(value); }}
                        options={runTypes?.data ?? []} />,
            }
        },
        {
            id: 'status',
            header: t.common.status,
            cell: (cell) => {
                if (cell.row.original.failed) {
                    return <span>{window.translation.workQueue.checkData}</span>;
                } else if (cell.row.original.completed) {
                    return <span>{window.translation.workQueue.completed}</span>;
                } else if (cell.row.original.queued) {
                    if (cell.row.original.startedJobs) {
                        return (<Progress total={cell.row.original.jobCount} size="small" color="blue" value={cell.row.original.completedJobs} progress precision={0} style={{ width: '100%' }}></Progress>);
                    } else {
                        return <span>{window.translation.workQueue.queued}</span>;
                    }
                } else {
                    return <span>{window.translation.workQueue.created}</span>;
                }
            },
            meta: {
                width: '15%',
                Filter: (column) => <Select
                    style={{ width: '250px' }}
                    placeholder={t.common.status}
                    clearable
                    value={column.getFilterValue()?.toString()}
                    onChange={(ev, { value }) => { column.setFilterValue(value); }}
                    options={statusOptions.map(option => { return { value: option.id, text: option.text } })} />,
            }
        },
        {
            header: t.workQueue.created,
            accessorKey: "created",
            cell: (cell) => <span>{moment(cell.row.original.created).locale(window.locale).format("l LTS")}</span>,
            meta: {
                collapsing: true,
                width: '15%'
            },
        },
    ], [t, runTypes.data]);

    const table = useBpReactTable2<WorkQueueItemModel>({
        queryKeyPrefix: 'wq',
        query: async (paging, sorting, searchValue, filters) => {
            const runTypeId = getFilterValue(filters, 'runTypeId');
            const status = getFilterValue(filters, 'status');
            const labId = getFilterValue(filters, 'labId');
            let pageData = await workQueueClient.page(labId as any, paging.pageSize, paging.pageIndex + 1, sorting[0]?.id, sorting[0]?.desc ? 'desc' : 'asc', runTypeId, status, searchValue);
            return { rowCount: pageData.totalRows, rows: pageData.items }
        },
        columnDefs: columns,
        renderSubComponent: (props) => <WorkQueueItem data={props.row.original} workersEnabled={true} />,
        getRowCanExpand: (row) => true,
        defaultSortingState: [{ id: 'created', desc: true }],
        stateMode: 'sessionStorage',
        id: 'wq',
    });

    return <BPReactTable2 {...table}
        fixed
        singleLine />;
}

